import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { type LWApiPublicSupportRequestCaseCreationPicklistOptionsResultRawI } from '@liquidweb-dev/club-sauce/public/support/request/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import api from 'modules/queries/api';

export interface CaseCreationPicklistOptions {
	originalData: LWApiPublicSupportRequestCaseCreationPicklistOptionsResultRawI;
	departments: { label: string; value: string }[];
	categoriesByDepartments: Record<string, { label: string; value: string }[]>;
	ticketCreationTypeByDepartment: Record<string, string>;
}

const supplementCaseCreationPicklistOptions = (
	data: LWApiPublicSupportRequestCaseCreationPicklistOptionsResultRawI,
) => {
	const departments = Object.values(data.details.desired_lw_team).map(
		(department) => ({
			label: department.display_name,
			value: department.value,
		}),
	);

	const categoriesByDepartments: Record<
		string,
		{ label: string; value: string }[]
	> = {};

	Object.values(data.details.desired_lw_team).forEach((department) => {
		const categories = Object.values(
			// there should only be one key in this object
			Object.values(department.types)[0].sub_types,
		);
		if (categories.length > 0) {
			categoriesByDepartments[department.value] = categories.map(
				(category) => ({
					value: category.value,
					label: category.display_name,
				}),
			);
		}
	});

	const ticketCreationTypeByDepartment: Record<string, string> = {};

	Object.values(data.details.desired_lw_team).forEach((desiredLwTeam) => {
		// there should only be one key in this object
		const ticketCreationType = Object.keys(desiredLwTeam.types)[0];
		ticketCreationTypeByDepartment[desiredLwTeam.value] = ticketCreationType;
	});

	return {
		originalData: data,
		departments,
		categoriesByDepartments,
		ticketCreationTypeByDepartment,
	};
};

export const SUPPORT_REQUEST_CASE_CREATION_PICKLIST_OPTIONS_QUERY_KEY =
	'/support/request/caseCreationPicklistOptions';

const useCaseCreationPicklistOptions = (
	options?: Omit<
		UseQueryOptions<
			LWApiPublicSupportRequestCaseCreationPicklistOptionsResultRawI,
			ApiErrorI,
			CaseCreationPicklistOptions
		>,
		'select'
	>,
) =>
	useQuery({
		queryKey: [SUPPORT_REQUEST_CASE_CREATION_PICKLIST_OPTIONS_QUERY_KEY],
		queryFn: () => api.support.request.apiRaw.caseCreationPicklistOptions(),
		staleTime: Infinity,
		select: supplementCaseCreationPicklistOptions,
		...options,
	});

export default useCaseCreationPicklistOptions;
